import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  TCustomCheckoutComponentItem,
  // TCustomCheckoutComponentType,
  TCustomCheckoutLayout,
  isContainerItem,
} from "../../core/types/CustomCheckout";
import CheckoutComponenetWidget from "./CheckoutComponentWidget";
import CheckoutComponenetList from "./CheckoutComponentList";
import { useCheckoutBuilder } from "../../core/context/CheckoutBuilderContext";

export interface ICheckoutLayout2_3Props {
  item: TCustomCheckoutComponentItem;
  parentList: TCustomCheckoutComponentItem[];
}

const CheckoutLayout2_3 = ({ item, parentList }: ICheckoutLayout2_3Props) => {
  const { layout, dragOverItem, dragOverItemOld, dropZoneTarget, setList } = useCheckoutBuilder();

  const handleSetList = (value: TCustomCheckoutComponentItem[]) => {
    // CORREÇÃO CRÍTICA: Atualiza APENAS o filho lateral (children[1]),
    // preservando o checkout (children[0]) e demais filhos.
    // Antes, substituía TODOS os children do layout2_3, perdendo o checkout.
    const updatedChildren = item.children.map((child, idx) => {
      if (idx === 1 && isContainerItem(child)) {
        // Atualiza os filhos da lateral com os novos valores
        return { ...child, children: [...value] };
      }
      return child;
    });

    const newItem: TCustomCheckoutComponentItem = {
      ...item,
      children: updatedChildren,
    };
    const newItems = parentList.map((list) =>
      list.id === newItem.id ? { ...newItem } : { ...list },
    );
    setList([...newItems]);
  };

  // Handler para quando o mouse entra na área lateral com a lista
  // Seta o dragOverItem para o primeiro filho da lista lateral (onde o componente será inserido)
  const handleLateralDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Limpa zona de drop fixa ao entrar na lateral
    dropZoneTarget.current = "";
    // Se a lateral (children[1]) existe e tem filhos, aponta para o primeiro filho
    if (item.children.length > 1 && item.children[1].children.length > 0) {
      dragOverItemOld.current = dragOverItem.current;
      dragOverItem.current = item.children[1].children[0].id;
    } else if (item.children.length > 1) {
      // Se a lateral existe mas está vazia, aponta para a própria lateral
      dragOverItemOld.current = dragOverItem.current;
      dragOverItem.current = item.children[1].id;
    }
  };

  const handleLateralDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handler para quando o mouse entra na área do checkout (lado esquerdo)
  // Redireciona o drop target para a lateral, impedindo que componentes substituam o checkout
  const handleCheckoutAreaDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Limpa zona de drop fixa ao entrar na área do checkout
    dropZoneTarget.current = "";
    // Redireciona para a lateral (children[1]) em vez de aceitar drop no checkout
    if (item.children.length > 1 && item.children[1].children.length > 0) {
      dragOverItemOld.current = dragOverItem.current;
      dragOverItem.current = item.children[1].children[0].id;
    } else if (item.children.length > 1) {
      dragOverItemOld.current = dragOverItem.current;
      dragOverItem.current = item.children[1].id;
    }
  };

  const handleCheckoutAreaDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (layout === TCustomCheckoutLayout.mobile) {
    return (
      <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
        {item.children.length > 0 && (
          <div onDragEnter={handleCheckoutAreaDragEnter} onDragOver={handleCheckoutAreaDragOver}>
            <CheckoutComponenetWidget item={item.children[0]} parentList={parentList} />
          </div>
        )}
        {item.children.length === 0 && (
          <Box
            onDragEnter={handleLateralDragEnter}
            onDragOver={handleLateralDragOver}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              width: "100%",
              height: 120,
              border: `2px dashed ${blue[500]}`,
              borderRadius: "8px",
              backgroundColor: blue[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pointerEvents: "none" }}
            >
              <AddCircleOutlineIcon sx={{ color: blue[500], fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: blue[700], fontWeight: 500 }}>
                Solte aqui para adicionar
              </Typography>
            </Stack>
          </Box>
        )}
        {item.children.length > 1 && item.children[1].children.length > 0 && (
          <Paper
            onDragEnter={handleLateralDragEnter}
            onDragOver={handleLateralDragOver}
            sx={{
              width: "100%",
              height: item.children[1]?.sx.height ?? 120,
              border: `2px solid ${blue[700]}`,
              borderStyle: "dashed",
              borderRadius: "8px",
              backgroundColor: "white",
              color: grey[800],
            }}
          >
            <Stack
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              <CheckoutComponenetList
                listOverride={item.children[1].children}
                setListOverride={(e) => handleSetList(e)}
              />
            </Stack>
          </Paper>
        )}
      </Stack>
    );
  }

  return (
    <Grid container columns={3} spacing={2}>
      <Grid item sm={3} md={2}>
        {item.children.length > 0 && (
          <div onDragEnter={handleCheckoutAreaDragEnter} onDragOver={handleCheckoutAreaDragOver}>
            <CheckoutComponenetWidget item={item.children[0]} parentList={parentList} />
          </div>
        )}
        {item.children.length === 0 && (
          <Box
            onDragEnter={handleLateralDragEnter}
            onDragOver={handleLateralDragOver}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              width: "100%",
              height: 120,
              border: `2px dashed ${blue[500]}`,
              borderRadius: "8px",
              backgroundColor: blue[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pointerEvents: "none" }}
            >
              <AddCircleOutlineIcon sx={{ color: blue[500], fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: blue[700], fontWeight: 500 }}>
                Solte aqui para adicionar
              </Typography>
            </Stack>
          </Box>
        )}
      </Grid>
      <Grid item sm={3} md={1}>
        {item.children.length > 1 && item.children[1].children.length <= 0 && (
          <Box
            onDragEnter={handleLateralDragEnter}
            onDragOver={handleLateralDragOver}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              width: "100%",
              height: item.children[1]?.sx.height ?? 120,
              border: `2px dashed ${blue[500]}`,
              borderRadius: "8px",
              backgroundColor: blue[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pointerEvents: "none" }}
            >
              <AddCircleOutlineIcon sx={{ color: blue[500], fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: blue[700], fontWeight: 500 }}>
                Solte aqui para adicionar
              </Typography>
            </Stack>
          </Box>
        )}
        {item.children.length > 1 && item.children[1].children.length > 0 && (
          <Paper
            onDragEnter={handleLateralDragEnter}
            onDragOver={handleLateralDragOver}
            sx={{
              width: "100%",
              height: item.children[1]?.sx.height ?? 120,
              border: `2px solid ${blue[700]}`,
              borderStyle: "dashed",
              borderRadius: "8px",
              backgroundColor: "white",
              color: grey[800],
            }}
          >
            <Stack
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              <CheckoutComponenetList
                listOverride={item.children[1].children}
                setListOverride={(e) => handleSetList(e)}
              />
            </Stack>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default CheckoutLayout2_3;
